import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import type { Extension } from "@codemirror/state";
import { tags } from "@lezer/highlight";

/**
 * Source-pane density presets — per-face sizing, not one global scale.
 *
 * Research / design notes (2026-07):
 * - **JetBrains Mono** is built for small–medium UI code; JetBrains recommends
 *   ~13px / 1.2 line spacing. Mono falls apart when blown up with prose.
 * - **Charter** (document serif) sings at larger sizes (~15–16px on screen /
 *   classic 10–12pt body). At 11–13px serifs go sharp and harsh on LCDs.
 * - **Inter** (UI sans) is optimized for small screen text; better content face
 *   for S/M densities than Charter.
 * - Dual size: structure mono stays near its design size while content grows
 *   on L — same pattern as “code samples in a paper” (code slightly smaller
 *   than surrounding prose).
 *
 * Product: source is visualization, not an IDE. S/M/L pick a *pair* of faces
 * at their best sizes, not “make everything 11 / 13 / 15.”
 */

export type SourceDensity = "compact" | "normal" | "comfortable";

export interface SourceDensityPreset {
  /** Short chip: S / M / L */
  label: string;
  /** Accessible / tooltip line */
  title: string;
  /** Shown under the chip in UI (“Small”, …) */
  name: string;
  /** Prose / content face (inside braces, titles, body). */
  contentFace: string;
  /** Commands, braces, comments, gutters. */
  structureFace: string;
  contentSize: string;
  structureSize: string;
  lineHeight: string;
  structureWeight: string;
  /** Soften mono at small sizes (no ligatures). */
  letterSpacing?: string;
}

const SERIF = "var(--lilia-font-serif)";
const SANS = "var(--lilia-font-sans)";
/** Wide, high x-height sans — soft at ~12px (better than Inter for S). */
const COMPACT_SANS = "var(--lilia-font-source-compact, var(--lilia-font-sans))";
const MONO = "var(--lilia-font-source, var(--lilia-font-mono))";

export const SOURCE_DENSITY_PRESETS: Record<SourceDensity, SourceDensityPreset> = {
  // S — Source Sans 3 (not Inter): wider counters, designed for small UI;
  // slightly larger content than structure; medium weight keeps ink density.
  compact: {
    label: "S",
    name: "Small",
    title: "Small — Source Sans 12.5 / Mono 12",
    contentFace: COMPACT_SANS,
    structureFace: MONO,
    contentSize: "12.5px",
    structureSize: "12px",
    lineHeight: "1.58",
    structureWeight: "500",
    letterSpacing: "0.015em",
  },
  normal: {
    label: "M",
    name: "Medium",
    title: "Medium — Inter 13.5 / Mono 12.5",
    contentFace: SANS,
    structureFace: MONO,
    contentSize: "13.5px",
    structureSize: "12.5px",
    lineHeight: "1.62",
    structureWeight: "500",
    letterSpacing: "0.005em",
  },
  comfortable: {
    label: "L",
    name: "Large",
    title: "Large — Charter 15.5 / Mono 13",
    contentFace: SERIF,
    structureFace: MONO,
    contentSize: "15.5px",
    structureSize: "13px",
    lineHeight: "1.72",
    structureWeight: "500",
    letterSpacing: "0",
  },
};

/** Default density: M — balanced Inter + mono; L remains one click for Charter. */
export const DEFAULT_SOURCE_DENSITY: SourceDensity = "normal";

export function isSourceDensity(v: unknown): v is SourceDensity {
  return v === "compact" || v === "normal" || v === "comfortable";
}

/** Base EditorView theme for a density (scroller, gutters, default face). */
export function sourceDensityBaseTheme(density: SourceDensity): Extension {
  const p = SOURCE_DENSITY_PRESETS[density] ?? SOURCE_DENSITY_PRESETS.comfortable;
  // Pane / gutter use --src-* from source-surface presets (fallback = code tokens).
  return EditorView.theme({
    "&": {
      backgroundColor: "var(--src-pane-bg, var(--lilia-code-bg))",
      color: "var(--lilia-text, var(--lilia-code-text))",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
    },
    ".cm-content": {
      caretColor: "var(--lilia-code-cursor)",
      padding: density === "comfortable" ? "18px 14px" : "14px 12px",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      lineHeight: p.lineHeight,
      backgroundColor: "transparent",
    },
    ".cm-line": {
      padding: "0 4px",
    },
    ".cm-scroller": {
      overflow: "auto",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      lineHeight: p.lineHeight,
      backgroundColor: "var(--src-pane-bg, var(--lilia-code-bg))",
    },
    ".cm-gutters": {
      backgroundColor: "var(--src-gutter-bg, var(--lilia-code-gutter-bg))",
      color: "var(--lilia-code-gutter)",
      border: "none",
      borderRight: "1px solid var(--lilia-border)",
      paddingRight: "8px",
      fontFamily: p.structureFace,
      fontSize: p.structureSize,
      fontVariantLigatures: "none",
      fontFeatureSettings: '"liga" 0, "calt" 0',
    },
    ".cm-activeLineGutter": {
      backgroundColor: "var(--lilia-code-active-line)",
    },
    ".cm-activeLine": {
      backgroundColor: "var(--lilia-code-active-line)",
    },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
      backgroundColor: "var(--lilia-code-selection)",
    },
  });
}

/** Highlight styles with per-role font + size for this density. */
export function sourceDensityHighlighting(density: SourceDensity): Extension {
  const p = SOURCE_DENSITY_PRESETS[density] ?? SOURCE_DENSITY_PRESETS.comfortable;
  const mono: Record<string, string> = {
    fontFamily: p.structureFace,
    fontSize: p.structureSize,
    fontVariantLigatures: "none",
    fontFeatureSettings: '"liga" 0, "calt" 0',
  };
  if (p.letterSpacing) mono.letterSpacing = p.letterSpacing;

  // Compact content: medium weight so Source Sans keeps presence at small size.
  const contentWeight = density === "compact" ? "500" : "400";

  const style = HighlightStyle.define([
    // Content — document face at content size
    {
      tag: tags.content,
      color: "var(--lilia-text, var(--lilia-code-text))",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      fontWeight: contentWeight,
    },
    // Structure — mono at structure size
    {
      tag: tags.keyword,
      color: "var(--lilia-code-command)",
      fontWeight: p.structureWeight,
      ...mono,
    },
    // \begin / \end: the skeleton, the one colour of its own (11 Oct).
    {
      tag: tags.controlKeyword,
      color: "var(--lilia-code-function)",
      fontWeight: "600",
      ...mono,
    },
    {
      tag: tags.operatorKeyword,
      color: "var(--lilia-code-command)",
      fontWeight: p.structureWeight,
      ...mono,
    },
    // Environment, label, citation and package names: what the reader scans
    // for (latex-language.ts). They wore the command colour here until 11 Oct.
    {
      tag: tags.tagName,
      color: "var(--lilia-code-tag)",
      fontWeight: p.structureWeight,
      ...mono,
    },
    // Section titles, bold and italic arguments: weight and slant, not colour.
    {
      tag: tags.heading,
      color: "var(--lilia-text, var(--lilia-code-text))",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      fontWeight: "700",
    },
    {
      tag: tags.strong,
      color: "var(--lilia-text, var(--lilia-code-text))",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      fontWeight: "700",
    },
    {
      tag: tags.emphasis,
      color: "var(--lilia-text, var(--lilia-code-text))",
      fontFamily: p.contentFace,
      fontSize: p.contentSize,
      fontStyle: "italic",
    },
    {
      tag: tags.macroName,
      color: "var(--lilia-code-command)",
      fontWeight: p.structureWeight,
      ...mono,
    },
    {
      tag: tags.comment,
      color: "var(--lilia-code-comment)",
      fontStyle: "italic",
      ...mono,
    },
    {
      tag: tags.lineComment,
      color: "var(--lilia-code-comment)",
      fontStyle: "italic",
      ...mono,
    },
    {
      tag: tags.brace,
      color: "var(--lilia-code-brace)",
      fontWeight: "500",
      ...mono,
    },
    {
      tag: tags.squareBracket,
      color: "var(--lilia-code-bracket)",
      ...mono,
    },
    {
      tag: tags.separator,
      color: "var(--lilia-code-separator)",
      ...mono,
    },
    {
      tag: tags.operator,
      color: "var(--lilia-code-separator)",
      ...mono,
    },
    {
      tag: tags.processingInstruction,
      color: "var(--lilia-code-mathdelim)",
      fontWeight: "500",
      ...mono,
    },
    {
      tag: tags.number,
      color: "var(--lilia-code-number)",
      ...mono,
    },
    {
      tag: tags.atom,
      color: "var(--lilia-code-bool)",
      fontStyle: "italic",
      ...mono,
    },
    {
      tag: tags.string,
      color: "var(--lilia-code-string)",
      ...mono,
    },
    {
      tag: tags.attributeName,
      color: "var(--lilia-code-attr-key)",
      ...mono,
    },
    {
      tag: tags.attributeValue,
      color: "var(--lilia-code-string)",
      ...mono,
    },
  ]);

  return syntaxHighlighting(style);
}

/** Theme + highlighting for a density (swap both via one compartment). */
export function sourceDensityExtensions(density: SourceDensity): Extension[] {
  return [sourceDensityBaseTheme(density), sourceDensityHighlighting(density)];
}
